"use client";

import { useRef, useEffect } from "react";

interface FittedTextProps {
  text: string;
  className?: string;
  style?: React.CSSProperties;
  lineHeight?: number;
  scaleY?: number; // 1 = normal, 0.8 = squashed, 1.2 = stretched
}

export default function FittedText({
  text,
  className = "",
  style = {},
  lineHeight = 1,
  scaleY = 1,
}: FittedTextProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    const textEl = textRef.current;
    if (!container || !textEl) return;

    const resize = () => {
      // 1. Reset to natural dimensions for measurement
      textEl.style.transform = "none";
      textEl.style.width = "auto";

      // 2. Measure dimensions
      const containerWidth = container.offsetWidth;
      const textWidth = textEl.offsetWidth;
      const textHeight = textEl.offsetHeight;

      if (textWidth === 0) return;

      // 3. Calculate Scales
      // scaleX: fits the text perfectly to the container width
      const scaleX = containerWidth / textWidth;

      // finalScaleY: combines the fit-to-width scale with your custom vertical distortion
      const finalScaleY = scaleX * scaleY;

      // 4. Apply Transform
      textEl.style.transformOrigin = "top left";
      textEl.style.transform = `scale(${scaleX}, ${finalScaleY})`;

      // 5. Update Container Height
      // Force the container to match the new distorted height so elements below don't overlap
      container.style.height = `${textHeight * finalScaleY}px`;
    };

    const observer = new ResizeObserver(resize);
    observer.observe(container);

    // Ensure fonts are loaded before calculating
    document.fonts.ready.then(resize);
    resize();

    return () => observer.disconnect();
  }, [text, lineHeight, scaleY]);

  return (
    <div
      ref={containerRef}
      className="w-full relative overflow-hidden"
      style={{ ...style }}
    >
      <div
        ref={textRef}
        className={`inline-block whitespace-nowrap origin-top-left ${className}`}
        style={{
          fontSize: "300px", // Much larger base size for better resolution when scaling
          lineHeight: lineHeight,
          willChange: "transform",
          WebkitFontSmoothing: "antialiased",
          MozOsxFontSmoothing: "grayscale",
          backfaceVisibility: "hidden",
          transform: "translateZ(0)",
          imageRendering: "crisp-edges",
        }}
      >
        {text}
      </div>
    </div>
  );
}
